import { useState, useRef } from 'react';
import { CheckCircle2, Download, RefreshCw, Maximize2 } from 'lucide-react';
import html2canvas from 'html2canvas';
import { Tooltip } from 'primereact/tooltip';
import { STATUS_CFG } from '../../../helpers/Project-Report/utils/statusConfig';
import ViewModeDropdown from './ViewModeDropdown';
import StatusFilterDropdown from './StatusFilterDropdown';

const GanttTab = ({ tasks, mode, accent, dueDate }) => {
  // mode = 'actual' | 'schedule'
  const startKey = mode === 'actual' ? 'actualStart' : 'scheduleStart';
  const endKey = mode === 'actual' ? 'actualEnd' : 'scheduleEnd';

  const [viewMode, setViewMode] = useState('weekly');
  const [selectedStatuses, setSelectedStatuses] = useState(Object.keys(STATUS_CFG));

  const getWeekNumber = (d) => {
    const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
    date.setUTCDate(date.getUTCDate() + 4 - (date.getUTCDay() || 7));
    const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
    const weekNo = Math.ceil((((date - yearStart) / 86400000) + 1) / 7);
    return { week: weekNo, year: date.getUTCFullYear() };
  };

  // Format Helper for inside the bar
  const formatDateGantt = (dStr) => {
    if (!dStr) return '';
    const d = new Date(dStr);
    const m = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${String(d.getDate()).padStart(2, '0')}-${m[d.getMonth()]}-${d.getFullYear()}`;
  };

  // Compute date range across all tasks
  const baseTasks = tasks.filter(t => t[startKey] && t[endKey]);
  if (baseTasks.length === 0) {
    return <div style={{ padding: '48px', textAlign: 'center', color: '#94a3b8', fontSize: '13px' }}>Tidak ada data gantt</div>;
  }

  let allDates = baseTasks.flatMap(t => [new Date(t[startKey]), new Date(t[endKey])]);
  if (dueDate) {
    allDates.push(new Date(dueDate));
  }
  let minDate = new Date(Math.min(...allDates));
  let maxDate = new Date(Math.max(...allDates));
  // Add some padding
  minDate.setDate(minDate.getDate() - 2);
  maxDate.setDate(maxDate.getDate() + 4);

  const totalDays = Math.ceil((maxDate - minDate) / (1000 * 60 * 60 * 24));

  // Build date labels
  const dateLabels = [];
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  if (viewMode === 'monthly') {
    let currentMonthStr = '';
    for (let i = 0; i <= totalDays; i++) {
      const d = new Date(minDate.getTime() + i * 86400000);
      const monthStr = `${monthNames[d.getMonth()]} ${d.getFullYear()}`;
      if (monthStr !== currentMonthStr) {
        dateLabels.push({ offset: i, label: monthStr });
        currentMonthStr = monthStr;
      }
    }
  } else if (viewMode === 'weekly') {
    let currentWeekStr = '';
    for (let i = 0; i <= totalDays; i++) {
      const d = new Date(minDate.getTime() + i * 86400000);
      const { week, year } = getWeekNumber(d);
      const weekStr = `Week ${week} (${year})`;
      if (weekStr !== currentWeekStr) {
        dateLabels.push({ offset: i, label: weekStr });
        currentWeekStr = weekStr;
      }
    }
  } else {
    for (let i = 0; i <= totalDays; i += 2) {
      const d = new Date(minDate.getTime() + i * 86400000);
      dateLabels.push({ offset: i, label: `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}` });
    }
  }

  // Markers
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Normalize to midnight to align perfectly with minDate/maxDate
  const todayOffset = Math.ceil((today - minDate) / (1000 * 60 * 60 * 24));
  const todayPct = (todayOffset / totalDays) * 100;

  let dueDatePct = -1;
  let due = null;
  if (dueDate) {
    due = new Date(dueDate);
    due.setHours(0, 0, 0, 0); // Normalize to midnight
    dueDatePct = ((due - minDate) / (maxDate - minDate)) * 100;
  }

  const validTasks = baseTasks.filter(t => selectedStatuses.includes(t.status));

  const taskBarColor = (statusKey) => {
    const config = STATUS_CFG[statusKey];
    if (config) return { bar: config.color };
    if (statusKey === 'done') return { bar: '#10b981' };
    return { bar: '#94a3b8' };
  };

  const LABEL_W = 200;
  const ROW_H = 48;

  // Interactions State
  const chartRef = useRef(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Handlers
  const handleRefresh = () => {
    setIsRefreshing(true);
    setTimeout(() => setIsRefreshing(false), 800);
  };

  const handleZoom = () => {
    // Cycles zoom 1 -> 1.5 -> 2 -> 1
    setZoomLevel(prev => prev >= 2 ? 1 : prev + 0.5);
  };

  const handleDownload = async () => {
    if (!chartRef.current) return;
    try {
      // Temporarily remove overflow to capture full chart
      const originalOverflow = chartRef.current.style.overflowX;
      chartRef.current.style.overflowX = 'visible';

      const canvas = await html2canvas(chartRef.current, { scale: 2 });

      chartRef.current.style.overflowX = originalOverflow; // restore

      const link = document.createElement('a');
      link.download = `gantt-chart-${mode}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (err) {
      console.error('Failed to download chart', err);
    }
  };

  return (
    <div>
      {/* Gantt header toolbar */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '800', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ width: '4px', height: '20px', borderRadius: '99px', background: accent }} />
          {mode === 'actual' ? 'Actual Gantt Chart' : 'Schedule Gantt Chart'} {zoomLevel > 1 && <span style={{ fontSize: '12px', color: '#64748b' }}>(Zoom {zoomLevel}x)</span>}
        </h3>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
          <StatusFilterDropdown
            value={selectedStatuses}
            onChange={setSelectedStatuses}
            accent={accent}
          />
          <ViewModeDropdown
            value={viewMode}
            onChange={setViewMode}
            accent={accent}
          />

          <button onClick={handleRefresh} style={{ width: '34px', height: '34px', borderRadius: '9px', background: accent, border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: `0 3px 8px ${accent}50`, transition: 'all 0.2s' }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.08)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; }}
            title="Refresh Data">
            <RefreshCw size={14} color="#fff" style={{ animation: isRefreshing ? 'spin 1s linear infinite' : 'none' }} />
          </button>

          <button onClick={handleZoom} style={{ width: '34px', height: '34px', borderRadius: '9px', background: accent, border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: `0 3px 8px ${accent}50`, transition: 'all 0.2s' }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.08)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; }}
            title="Zoom Chart">
            <Maximize2 size={14} color="#fff" />
          </button>

          <button onClick={handleDownload} style={{ width: '34px', height: '34px', borderRadius: '9px', background: accent, border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: `0 3px 8px ${accent}50`, transition: 'all 0.2s' }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.08)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; }}
            title="Download Chart">
            <Download size={14} color="#fff" />
          </button>
        </div>
      </div>

      <style>{`
        @keyframes spin { 100% { transform: rotate(360deg); } }
        .gantt-bar-wrapper {
          position: absolute;
        }
        .gantt-bar-wrapper:hover {
          z-index: 50 !important;
        }
        /* Sticky Column Styles */
        .gantt-task-label {
          position: sticky;
          left: 0;
          background: #fff;
          z-index: 60;
          border-right: 1px solid #f1f5f9;
          display: flex;
          align-items: center;
          height: 100%;
          box-sizing: border-box;
        }
      `}</style>

      {/* Chart area */}
      <div ref={chartRef} style={{ overflowX: 'auto', background: '#fff', borderRadius: '14px', border: '1px solid #f1f5f9', boxShadow: '0 2px 12px rgba(0,0,0,0.05)', opacity: isRefreshing ? 0.6 : 1, transition: 'opacity 0.3s' }}>
        <div style={{ minWidth: `${Math.max(700, totalDays * (viewMode === 'monthly' ? 4 : viewMode === 'weekly' ? 12 : 25) * zoomLevel)}px`, padding: '20px', paddingTop: '36px', position: 'relative' }}>
          {/* Date axis */}
          <div style={{ display: 'flex', marginBottom: '8px', marginLeft: `${LABEL_W}px`, paddingRight: '30px' }}>
            <div style={{ flex: 1, position: 'relative', height: '36px' }}>
              {dateLabels.map((dl, i) => (
                <div
                  key={i}
                  style={{
                    position: 'absolute',
                    left: `${(dl.offset / totalDays) * 100}%`,
                    top: '4px',
                    fontSize: '9px',
                    color: '#94a3b8',
                    whiteSpace: 'nowrap',
                    transform: 'rotate(-35deg)',
                    transformOrigin: 'left center',
                    zIndex: 10,
                  }}
                >
                  {dl.label}
                </div>
              ))}
            </div>
          </div>

          {/* Task rows */}
          <div style={{ position: 'relative' }}>
            {/* Global Lines Container (overlaps rows inside task container to avoid overflowing main container) */}
            <div style={{ position: 'absolute', top: 0, bottom: 0, left: `${LABEL_W}px`, right: '30px', zIndex: 1, pointerEvents: 'none' }}>
              {/* Today line */}
              {todayPct >= 0 && todayPct <= 100 && (
                <div style={{ position: 'absolute', left: `${todayPct}%`, top: '-30px', bottom: 0, width: '0px', borderLeft: '2px dashed #f97316' }}>
                  <div
                    title={`Today: ${formatDateGantt(today.toISOString())}`}
                    style={{ position: 'absolute', top: 12, left: '-50%', transform: 'translateX(-50%)', background: '#f97316', color: '#fff', fontSize: '9px', fontWeight: 'bold', padding: '2px 6px', borderRadius: '4px', whiteSpace: 'nowrap', pointerEvents: 'auto' }}
                  >
                    Today
                  </div>
                </div>
              )}

              {/* Due Date line */}
              {dueDatePct >= 0 && dueDatePct <= 100 && (
                <div style={{ position: 'absolute', left: `${dueDatePct}%`, top: '-30px', bottom: 0, width: '0px', borderLeft: '2px dashed #000' }}>
                  <div
                    title={`Due Date: ${due ? formatDateGantt(due.toISOString()) : ''}`}
                    style={{ position: 'absolute', top: -10, left: '-50%', transform: 'translateX(-50%)', background: '#000', color: '#fff', fontSize: '9px', fontWeight: 'bold', padding: '2px 6px', borderRadius: '4px', whiteSpace: 'nowrap', pointerEvents: 'auto' }}
                  >
                    Due Date
                  </div>
                  <div style={{ position: 'absolute', top: 5, left: '-4px', width: 0, height: 0, borderLeft: '4px solid transparent', borderRight: '4px solid transparent', borderTop: '6px solid #000' }}></div>
                </div>
              )}
            </div>

            <Tooltip
              target=".gantt-label-tooltip"
              position="top"
              mouseTrack
              mouseTrackTop={15}
              pt={{ text: { style: { fontSize: '11px', padding: '6px 10px', fontWeight: '500', maxWidth: '260px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' } } }}
            />
            <Tooltip
              target=".gantt-bar-tooltip"
              position="top"
              mouseTrack
              mouseTrackTop={15}
              pt={{ text: { style: { fontSize: '11px', padding: '6px 10px', fontWeight: '500', maxWidth: '260px', whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: '1.6' } } }}
            />

            {validTasks.length === 0 && (
              <div style={{ padding: '32px', textAlign: 'center', color: '#94a3b8', fontSize: '13px', background: '#f8fafc', borderRadius: '8px', margin: '16px 0' }}>
                Tidak ada task dengan status tersebut.
              </div>
            )}

            {validTasks.map((task) => {
              const s = new Date(task[startKey]);
              const e = new Date(task[endKey]);
              const durationDays = Math.ceil((e - s) / (1000 * 60 * 60 * 24));
              const isLongDuration = durationDays >= 14;
              const pct = ((s - minDate) / (maxDate - minDate)) * 100;
              const wid = ((e - s) / (maxDate - minDate)) * 100;
              const { bar } = taskBarColor(task.status);
              const st = STATUS_CFG[task.status] || {};

              return (
                <div key={task.id} style={{ display: 'flex', alignItems: 'center', height: `${ROW_H}px`, borderBottom: '1px solid #f8fafc' }}>
                  {/* Task label (Sticky) */}
                  <div className="gantt-task-label" style={{ width: LABEL_W, flexShrink: 0, paddingRight: '12px', paddingLeft: '20px', marginLeft: '-20px' }}>
                    <div
                      className="gantt-label-tooltip"
                      data-pr-tooltip={task.name}
                      style={{ fontSize: '12px', color: '#374151', fontWeight: '500', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'default', width: '100%' }}
                    >
                      {task.name}
                    </div>
                  </div>

                  {/* Bar track */}
                  <div style={{ flex: 1, height: '28px', position: 'relative', borderRadius: '6px', background: '#f8fafc' }}>
                    {/* Grid lines (weekly or every 2 days) */}
                    {dateLabels.map((dl, i) => (
                      <div key={i} style={{ position: 'absolute', left: `${(dl.offset / totalDays) * 100}%`, top: 0, bottom: 0, width: '1px', background: '#e2e8f0' }} />
                    ))}

                    {/* Gantt bar container wrapper */}
                    <div
                      className="gantt-bar-wrapper gantt-bar-tooltip"
                      data-pr-tooltip={`${task.name}\n\nMulai: ${formatDateGantt(task[startKey])}\nSelesai: ${formatDateGantt(task[endKey])}\nProgress: ${task.progress}%`}
                      style={{
                        position: 'absolute',
                        left: `${pct}%`,
                        width: `${Math.max(wid, 1.5)}%`,
                        top: '4px',
                        height: '20px',
                        zIndex: 2,
                        cursor: 'pointer'
                      }}
                    >
                      <div
                        style={{
                          width: '100%',
                          height: '100%',
                          background: task.progress === 100 ? (STATUS_CFG['done']?.color || '#10b981') : `${bar}20`,
                          backgroundColor: task.progress === 100 ? (STATUS_CFG['done']?.color || '#10b981') : '#fee2e2',
                          borderRadius: '5px',
                          boxShadow: task.progress === 100 ? `0 2px 6px ${(STATUS_CFG['done']?.color || '#10b981')}60` : 'none',
                          overflow: 'hidden',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          position: 'relative'
                        }}
                      >
                        {/* Inner Progress Layer */}
                        {task.progress < 100 && (
                          <div style={{
                            position: 'absolute', top: 0, left: 0,
                            height: '100%',
                            width: `${task.progress}%`,
                            background: bar,
                            zIndex: 1
                          }} />
                        )}

                        {/* Remaining Space Alert Layer */}
                        {isLongDuration && task.progress > 0 && task.progress < 100 && (
                          <div style={{ position: 'absolute', top: 0, bottom: 0, left: `${task.progress}%`, right: '65px', paddingLeft: '8px', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', zIndex: 3, overflow: 'hidden' }}>
                            {wid > 15 && (100 - task.progress) >= 10 && (
                              <span style={{ fontSize: '9px', fontWeight: '700', color: '#ef4444' }}>
                                {100 - task.progress}%
                              </span>
                            )}
                          </div>
                        )}

                        {/* Start Date Text */}
                        {isLongDuration && (
                          <span style={{ position: 'relative', zIndex: 5, fontSize: '9px', fontWeight: '600', color: '#fff', paddingLeft: '6px', whiteSpace: 'nowrap', textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>
                            {formatDateGantt(task[startKey])}
                          </span>
                        )}

                        {/* Progress text overlay (center) */}
                        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none', zIndex: 5 }}>
                          {(wid > 15 || !isLongDuration) && (
                            <span style={{ fontSize: '9px', fontWeight: '700', color: (task.progress < 100 && task.progress < 50) ? '#1f2937' : '#fff', padding: '0 4px', textShadow: (task.progress < 100 && task.progress < 50) ? 'none' : '0 1px 2px rgba(0,0,0,0.3)' }}>
                              {task.progress}%
                            </span>
                          )}
                        </div>

                        {/* End Date Text */}
                        {isLongDuration && (
                          <span style={{ position: 'relative', zIndex: 5, fontSize: '9px', fontWeight: '600', color: task.progress === 100 ? '#fff' : '#ef4444', paddingRight: '6px', whiteSpace: 'nowrap' }}>
                            {formatDateGantt(task[endKey])}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Status icon */}
                  <div style={{ width: '30px', display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
                    <div
                      style={{
                        width: '22px', height: '22px', borderRadius: '50%',
                        background: task.status === 'done' ? (st.color || '#10b981') : `${st.color || '#94a3b8'}20`,
                        border: `2px solid ${st.color || (task.status === 'done' ? '#10b981' : '#94a3b8')}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}
                    >
                      {task.status === 'done'
                        ? <CheckCircle2 size={11} color="#fff" />
                        : <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: st.color || '#94a3b8' }} />}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Legend */}
      <div style={{ marginTop: '16px', padding: '20px', background: '#fff', borderRadius: '14px', border: '1px solid #f1f5f9' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
          <span style={{ fontSize: '14px' }}>📊</span>
          <span style={{ fontSize: '13px', fontWeight: '700', color: '#0f172a' }}>Task Status Legend</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '10px' }}>
          {Object.entries(STATUS_CFG).map(([key, val]) => (
            <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', borderRadius: '8px', background: '#f8fafc', border: '1px solid #f1f5f9' }}>
              <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: val.color, flexShrink: 0 }} />
              <span style={{ fontSize: '11.5px', color: '#374151', fontWeight: '500' }}>{val.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default GanttTab;
